import { AppDispatch, persistor } from '../../store';
import { errMsg } from '../../data/constants';
import { setLoading, setError, setSuccess } from '../app/appSlice';
import {
  loginSuccess,
  logoutSuccess,
  registerSuccess,
  setBillingSummary,
} from './authSlice';
import {
  AuthPayload,
  AuthController
} from '../../api/controllers/auth.controller';
import { firebase } from '../../utils/firebase';
import { BillingController } from '../../api/controllers/billing.controller';
import { identifyUser, resetUser, trackEvent, bridgeToGTM, ANALYTICS_EVENTS } from '../../lib/posthog';

export const login = (payload: AuthPayload) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(''));
    const response = await AuthController.login(payload);
    const { user, accessToken } = response.data;

    dispatch(loginSuccess({ user, accessToken }));
    identifyUser(user.id, { email: user.email, name: user.name, role: user.role });
    trackEvent(ANALYTICS_EVENTS.SIGN_IN, { method: 'email' });
    bridgeToGTM();
    return user;
  } catch (error: any) {
    if (import.meta.env.DEV) console.error('Auth error:', error?.code || 'Unknown');
    trackEvent(ANALYTICS_EVENTS.SIGN_IN_FAILED, { method: 'email' });
    let err = null;
    if (error.code && (errMsg as any)[error.code]) err = (errMsg as any)[error.code];
    else err = 'Login failed. please try it again later';
    dispatch(setError(err));
    return null;
  } finally {
    dispatch(setLoading(false));
  }
};

export const register = (payload: AuthPayload) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(''));
    const response = await AuthController.register(payload);
    const { user, accessToken } = response.data;

    // If backend returns accessToken, use loginSuccess to fully authenticate
    // Otherwise fall back to registerSuccess (pending state)
    if (accessToken) {
      dispatch(loginSuccess({ user, accessToken }));
    } else {
      dispatch(registerSuccess({ user }));
    }

    identifyUser(user.id, { email: user.email, name: user.name, role: user.role, status: user.status });
    trackEvent(ANALYTICS_EVENTS.SIGN_UP, { method: 'email' });
    bridgeToGTM();
    return { user, accessToken };
  } catch (error: any) {
    if (import.meta.env.DEV) console.error('Auth error:', error?.code || 'Unknown');
    let err = null;
    if (error.code && (errMsg as any)[error.code]) err = (errMsg as any)[error.code];
    else if (error?.response?.data?.error) err = error.response.data.error;
    else err = 'Registration failed. Please try again.';
    dispatch(setError(err));
    return { error: err };
  } finally {
    dispatch(setLoading(false));
  }
};

export const socialLogin = (payload: {
  credential: firebase.auth.UserCredential;
  name?: string;
  orgName?: string;
  barLicenses?: { state: string; barNumber: string; isPrimary: boolean }[];
  officeStreet?: string;
  officeCity?: string;
  officeState?: string;
  officeZip?: string;
}) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(''));
    const response = await AuthController.socialLogin(payload);
    const { user, accessToken } = response.data;

    dispatch(loginSuccess({ user, accessToken }));
    identifyUser(user.id, { email: user.email, name: user.name, role: user.role });
    trackEvent(ANALYTICS_EVENTS.SIGN_IN, { method: 'google' });
    bridgeToGTM();
    return user;
  } catch (error: any) {
    if (import.meta.env.DEV) console.error('Auth error:', error?.code || 'Unknown');
    trackEvent(ANALYTICS_EVENTS.SIGN_IN_FAILED, { method: 'google' });
    let err = null;
    if (error.code && (errMsg as any)[error.code]) err = (errMsg as any)[error.code];
    else if (error?.response?.data?.error) err = error.response.data.error;
    else err = `Social Login failed. please try again later`;
    dispatch(setError(err));
    return {
      error: err,
    };
  } finally {
    dispatch(setLoading(false));
  }
};

export const resetPassword = (email: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(''));
    await AuthController.resetPassword(email);
    dispatch(setSuccess(true));
  } catch (error: any) {
    if (import.meta.env.DEV) console.error('Auth error:', error?.code || 'Unknown');
    let err = null;
    if (error.code && (errMsg as any)[error.code]) err = (errMsg as any)[error.code];
    else err = `Could not send reset link. Please try again later.`;
    dispatch(setError(err));
    dispatch(setSuccess(false));
  } finally {
    dispatch(setLoading(false));
  }
};

export const logout = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(setError(''));
    await AuthController.logout();
    trackEvent(ANALYTICS_EVENTS.SIGN_OUT);
    resetUser();
    dispatch(logoutSuccess());
    await persistor.purge();
  } catch (error: any) {
    if (import.meta.env.DEV) console.error('Auth error:', error?.code || 'Unknown');
    let err = null;
    if (error.code && (errMsg as any)[error.code]) err = (errMsg as any)[error.code];
    else err = 'Logout failed. please try it again later';
    dispatch(setError(err));
  }
};

export const fetchBillingSummary = () => async (dispatch: AppDispatch) => {
  try {
    const { data } = await BillingController.getStatus();
    if (data) {
      dispatch(
        setBillingSummary({
          billing_status: data.billing_status,
          billing_threshold: data.billing_threshold,
          current_unbilled_spend: data.current_unbilled_spend,
        })
      );
    }
  } catch (error: any) {
    if (import.meta.env.DEV) console.error('Billing error:', error?.code || 'Unknown');
  }
};
